import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface UserNotification {
  id: string;
  type: "charge" | "carrier" | "sender";
  title: string;
  message: string;
  created_at: string;
  read: boolean;
  amount?: number;
}

interface UserNotificationBellProps {
  userId: string;
  onToast?: (toast: { type: "success" | "error"; message: string; amount?: number }) => void;
  onClearAll?: (count: number) => void;
  onCreditUpdate?: (balance: number) => void;
  className?: string;
  dropdownClassName?: string;
}

export default function UserNotificationBell({ userId, onToast, onClearAll, onCreditUpdate, className, dropdownClassName }: UserNotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNew, setHasNew] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const addNotification = useCallback((notif: Omit<UserNotification, "id" | "read" | "created_at">) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newNotif: UserNotification = {
      ...notif,
      id,
      read: false,
      created_at: new Date().toISOString(),
    };
    setNotifications((prev) => [newNotif, ...prev].slice(0, 20));
    setUnreadCount((prev) => prev + 1);
    setHasNew(true);
    setTimeout(() => setHasNew(false), 3000);
  }, []);

  // 초기 알림 로드 (Supabase에서 과거 데이터 가져오기)
  const fetchInitial = useCallback(async () => {
    try {
      // 최근 충전 승인/거절 내역
      const { data: txData } = await supabase
        .from("credit_transactions")
        .select("id, amount, status, created_at, type")
        .eq("user_id", userId)
        .in("status", ["approved", "rejected"])
        .order("created_at", { ascending: false })
        .limit(5);

      const txNotifications: UserNotification[] = (txData ?? []).map((tx: any) => ({
        id: `tx-${tx.id}`,
        type: "charge" as const,
        title: tx.status === "approved" ? "충전 완료" : "충전 거절",
        message:
          tx.status === "approved"
            ? `${Number(tx.amount).toLocaleString("ko-KR")}원 충전이 승인되었습니다.`
            : "충전 요청이 거절되었습니다.",
        created_at: tx.created_at,
        read: true,
        amount: tx.amount,
      }));

      // 최근 3사 테스트 판정 내역
      const { data: testData } = await supabase
        .from("carrier_tests")
        .select("id, carrier, review_results, created_at")
        .eq("user_id", userId)
        .not("review_results", "is", null)
        .order("created_at", { ascending: false })
        .limit(5);

      const testNotifications: UserNotification[] = [];
      (testData ?? []).forEach((test: any) => {
        const reviews = Array.isArray(test.review_results) ? test.review_results : [];
        reviews.forEach((review: any) => {
          if (review && review.carrier && review.result) {
            testNotifications.push({
              id: `test-${test.id}-${review.carrier}`,
              type: "carrier" as const,
              title: `${review.carrier} 3사 테스트 결과`,
              message: `판정 결과: ${review.result === "success" ? "성공" : "스팸"}`,
              created_at: test.created_at,
              read: true,
            });
          }
        });
      });

      const combined = [...txNotifications, ...testNotifications]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);

      setNotifications(combined);
      setUnreadCount(0);
    } catch (e) {
      console.error("[UserNotificationBell] 초기 알림 로딩 실패:", e);
    }
  }, [userId]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // 실시간 알림: postgres_changes로 credit_transactions 변경 감지
  useEffect(() => {
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const createChannel = () => {
      if (!active) return;

      const channel = supabase
        .channel(`user-credit-changes-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "credit_transactions",
            filter: `user_id=eq.${userId}`,
          },
          (payload: any) => {
            const oldRecord = payload.old;
            const newRecord = payload.new;

            // pending → approved/rejected 만 알림
            if (!oldRecord || !newRecord) return;
            if (oldRecord.status !== "pending") return;

            if (newRecord.status === "approved") {
              const amount = Number(newRecord.amount);
              addNotification({
                type: "charge",
                title: "충전 완료",
                message: `${amount.toLocaleString("ko-KR")}원 충전이 승인되었습니다.`,
                amount,
              });
              if (onToast) {
                onToast({
                  type: "success",
                  message: `${amount.toLocaleString("ko-KR")}원 충전이 승인되었습니다.`,
                  amount,
                });
              }
              if (newRecord.balance_after !== undefined && newRecord.balance_after !== null && onCreditUpdate) {
                onCreditUpdate(Number(newRecord.balance_after));
              }
            } else if (newRecord.status === "rejected") {
              addNotification({
                type: "charge",
                title: "충전 거절",
                message: "충전 요청이 거절되었습니다.",
              });
              if (onToast) {
                onToast({
                  type: "error",
                  message: "충전 요청이 거절되었습니다.",
                });
              }
            }
          },
        )
        .subscribe((status) => {
          if (!active) return;

          if (status === "SUBSCRIBED") {
            console.log("[UserNotificationBell] postgres_changes 구독 완료");
          } else if (status === "CHANNEL_ERROR") {
            console.warn("[UserNotificationBell] 채널 일시 오류 - SDK 자동 복구 대기 중");
          } else if (status === "CLOSED") {
            // 채널이 닫히면 3초 후 재구독 시도
            console.warn("[UserNotificationBell] 채널 닫힘 - 3초 후 재구독");
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => {
              if (active) createChannel();
            }, 3000);
          }
        });

      return channel;
    };

    let currentChannel = createChannel();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (currentChannel) supabase.removeChannel(currentChannel);
    };
  }, [userId, addNotification, onToast, onCreditUpdate]);

  // 실시간 알림: postgres_changes로 sender_numbers 변경 감지 (발신번호 승인/반려)
  useEffect(() => {
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const createChannel = () => {
      if (!active) return;

      const channel = supabase
        .channel(`user-sender-changes-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "sender_numbers",
            filter: `user_id=eq.${userId}`,
          },
          (payload: any) => {
            const oldRecord = payload.old;
            const newRecord = payload.new;

            if (!oldRecord || !newRecord) return;
            if (oldRecord.status !== "pending") return;

            if (newRecord.status === "approved") {
              addNotification({
                type: "sender",
                title: "발신번호 승인 완료",
                message: `발신번호 ${newRecord.number}(${newRecord.name}) 심사가 승인되었습니다.`,
              });
              if (onToast) {
                onToast({
                  type: "success",
                  message: `발신번호 ${newRecord.number} 심사가 승인되었습니다.`,
                });
              }
            } else if (newRecord.status === "rejected") {
              const reason = newRecord.reject_reason ? ` 사유: ${newRecord.reject_reason}` : "";
              addNotification({
                type: "sender",
                title: "발신번호 심사 반려",
                message: `발신번호 ${newRecord.number}(${newRecord.name}) 심사가 반려되었습니다.${reason}`,
              });
              if (onToast) {
                onToast({
                  type: "error",
                  message: `발신번호 ${newRecord.number} 심사가 반려되었습니다.`,
                });
              }
            }
          },
        )
        .subscribe((status) => {
          if (!active) return;

          if (status === "SUBSCRIBED") {
            console.log("[UserNotificationBell] sender_numbers 구독 완료");
          } else if (status === "CHANNEL_ERROR") {
            console.warn("[UserNotificationBell] sender 채널 일시 오류 - SDK 자동 복구 대기 중");
          } else if (status === "CLOSED") {
            console.warn("[UserNotificationBell] sender 채널 닫힘 - 3초 후 재구독");
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => {
              if (active) createChannel();
            }, 3000);
          }
        });

      return channel;
    };

    let currentChannel = createChannel();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (currentChannel) supabase.removeChannel(currentChannel);
    };
  }, [userId, addNotification, onToast]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClickBell = () => {
    setOpen(!open);
    if (!open) {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  };

  const handleNotificationClick = (notif: UserNotification) => {
    setOpen(false);
    if (notif.type === "charge") {
      navigate("/settings");
    } else if (notif.type === "sender") {
      navigate("/dashboard/settings?tab=sender");
    } else {
      navigate("/carrier-test");
    }
  };

  const handleClearAll = () => {
    const deletedCount = notifications.length;
    setNotifications([]);
    setUnreadCount(0);
    if (onClearAll) onClearAll(deletedCount);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return "방금 전";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    return d.toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleClickBell}
        className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
          hasNew ? "bg-pink-500/20 text-pink-400" : "text-gray-400 hover:text-white hover:bg-white/[0.1]"
        } ${className ?? ""}`}
      >
        <i className="ri-notification-3-line text-lg"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-pink-500 text-white text-[10px] font-bold rounded-full border-2 border-[#0a0a18]">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute right-0 top-12 ${dropdownClassName ?? "w-80"} bg-[#0f0f1e] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden z-50`}>
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <h3 className="text-white text-sm font-semibold">알림</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-gray-500 text-xs hover:text-red-400 cursor-pointer transition-colors whitespace-nowrap"
                >
                  모두 삭제
                </button>
              )}
              {unreadCount > 0 && (
                <span className="text-pink-400 text-xs font-medium bg-pink-500/10 px-2 py-0.5 rounded-full">
                  {unreadCount}개 미확인
                </span>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <i className="ri-checkbox-circle-line text-2xl text-gray-600 mb-2"></i>
                <p className="text-gray-500 text-sm">새로운 알림이 없습니다</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] last:border-b-0 cursor-pointer ${
                    !n.read ? "bg-white/[0.02]" : ""
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${
                        n.type === "charge" ? "bg-amber-500/10" : n.type === "sender" ? "bg-sky-500/10" : "bg-emerald-500/10"
                      }`}
                    >
                      <i
                        className={`${
                          n.type === "charge"
                            ? "ri-coin-line text-amber-400"
                            : n.type === "sender"
                            ? "ri-phone-line text-sky-400"
                            : "ri-smartphone-line text-emerald-400"
                        } text-sm`}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {n.title}
                        {n.type === "charge" && n.amount && (
                          <span className="text-amber-400 ml-1">
                            {Number(n.amount).toLocaleString("ko-KR")}원
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">{n.message}</p>
                      <p className="text-gray-600 text-[10px] mt-1">{formatTime(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 bg-pink-500 rounded-full shrink-0 mt-1.5"></span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-white/[0.06] bg-white/[0.02]">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/settings");
              }}
              className="w-full text-center text-gray-400 text-xs font-medium hover:text-white cursor-pointer transition-colors"
            >
              전체 알림 보기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}